import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, CATEGORY_META, fmtPct } from "../lib/api";
import FiltersPanel, { DEFAULTS } from "../components/FiltersPanel";
import ScreenerTable from "../components/ScreenerTable";
import PresetBar from "../components/PresetBar";
import { Button } from "../components/ui/button";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { ArrowRight, ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

const PAGE_SIZE_OPTIONS = [50, 100, 200, 500];

export default function Dashboard() {
    const [meta, setMeta] = useState(null);
    const [filters, setFilters] = useState(DEFAULTS);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(200);
    const [topPicks, setTopPicks] = useState([]);
    const [watchlist, setWatchlist] = useState(new Set());
    const [sort, setSort] = useState({ field: "growth_streak", dir: "desc" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const [m, top, wl] = await Promise.all([
                api.get("/stocks/meta"),
                api.get("/stocks/top", { params: { limit: 6 } }),
                api.get("/watchlist").catch(() => ({ data: { items: [] } })),
            ]);
            setMeta(m.data);
            setTopPicks(top.data.items);
            setWatchlist(new Set((wl.data.items || []).map((x) => x.ticker)));
        })();
    }, []);

    const params = useMemo(() => {
        const p = { sort_by: sort.field, sort_dir: sort.dir, limit: pageSize, offset: page * pageSize };
        if (filters.categories.length) p.category = filters.categories.join(",");
        if (filters.sectors.length)    p.sector   = filters.sectors.join(",");
        if (filters.search)            p.search   = filters.search;
        const numFields = [
            "min_yield", "max_yield", "max_payout", "min_dgr_5y", "min_cagr_5y",
            "min_streak", "max_pe", "min_market_cap",
        ];
        numFields.forEach((k) => {
            if (filters[k] !== "" && filters[k] != null && !isNaN(Number(filters[k]))) {
                p[k] = Number(filters[k]);
            }
        });
        return p;
    }, [filters, sort, page, pageSize]);

    // Reset to first page whenever filters, sort or page size change
    useEffect(() => { setPage(0); }, [filters, sort, pageSize]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        const t = setTimeout(async () => {
            try {
                const res = await api.get("/stocks", { params });
                if (!cancelled) {
                    setRows(res.data.items);
                    setTotal(res.data.total ?? res.data.items.length);
                }
            } catch (e) {
                toast.error("Failed to load stocks");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 200);
        return () => { cancelled = true; clearTimeout(t); };
    }, [params]);

    const handleSort = useCallback((field) => {
        setSort((prev) => prev.field === field
            ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
            : { field, dir: field === "ticker" || field === "name" ? "asc" : "desc" });
    }, []);

    const toggleWatch = useCallback(async (ticker, isWatched) => {
        const next = new Set(watchlist);
        try {
            if (isWatched) {
                await api.delete(`/watchlist/${ticker}`);
                next.delete(ticker);
                toast.success(`Removed ${ticker} from watchlist`);
            } else {
                await api.post("/watchlist", { ticker });
                next.add(ticker);
                toast.success(`Added ${ticker} to watchlist`);
            }
            setWatchlist(next);
        } catch (e) {
            toast.error("Could not update watchlist");
        }
    }, [watchlist]);

    return (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
            <div className="mb-10 animate-fade-up">
                <div className="overline">Dashboard</div>
                <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
                    Dividend growth screener
                </h1>
                <p className="mt-2 text-muted-foreground">
                    {meta ? `${meta.total} dividend growth companies across ${meta.sectors.length} sectors.` : "Loading…"}
                </p>
            </div>

            {/* TOP PICKS */}
            {topPicks.length > 0 && (
                <section className="mb-12 animate-fade-up [animation-delay:80ms]">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-primary" />
                            <h2 className="font-heading text-lg font-semibold">Top picks · Kings & Aristocrats by 5Y CAGR</h2>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
                        {topPicks.map((s) => {
                            const cm = CATEGORY_META[s.category];
                            return (
                                <Link
                                    key={s.ticker}
                                    to={`/stock/${s.ticker}`}
                                    className="rounded-xl border border-border bg-background p-4 lift block"
                                    data-testid={`top-pick-${s.ticker}`}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="font-heading font-bold tracking-tight">{s.ticker}</div>
                                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${cm?.cls}`}>{cm?.label}</span>
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground truncate">{s.name}</div>
                                    <div className="mt-4 flex items-baseline gap-1">
                                        <span className="font-heading text-xl font-bold tabular-nums text-emerald-700">{fmtPct(s.cagr_5y, 1)}</span>
                                        <span className="text-[10px] text-muted-foreground">5Y CAGR</span>
                                    </div>
                                    <div className="mt-1 text-xs text-muted-foreground tabular-nums">
                                        {fmtPct(s.yield)} · {s.growth_streak}y streak
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* MAIN GRID */}
            <div className="flex flex-col lg:flex-row gap-8">
                <FiltersPanel meta={meta} value={filters} onChange={setFilters} />

                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-3 text-sm gap-3 flex-wrap">
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="text-muted-foreground tabular-nums" data-testid="results-count">
                                {loading
                                    ? "Loading…"
                                    : total === 0
                                        ? "0 results"
                                        : `${page * pageSize + 1}–${page * pageSize + rows.length} of ${total.toLocaleString("en-US")}`}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>Show</span>
                                <Select
                                    value={String(pageSize)}
                                    onValueChange={(v) => setPageSize(Number(v))}
                                >
                                    <SelectTrigger className="h-8 w-[78px]" data-testid="page-size-trigger">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent data-testid="page-size-menu">
                                        {PAGE_SIZE_OPTIONS.map((n) => (
                                            <SelectItem
                                                key={n}
                                                value={String(n)}
                                                data-testid={`page-size-${n}`}
                                            >
                                                {n}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span>per page</span>
                            </div>
                            {total > pageSize && (
                                <InlinePager
                                    page={page}
                                    total={total}
                                    pageSize={pageSize}
                                    onPage={(p) => {
                                        setPage(p);
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                />
                            )}
                        </div>
                        <div className="flex items-center gap-3">
                            <PresetBar
                                filters={filters}
                                sort={sort}
                                onApply={({ filters: f, sort: s }) => {
                                    setFilters({ ...DEFAULTS, ...f });
                                    if (s?.field && s?.dir) setSort(s);
                                }}
                            />
                            <Link to="/watchlist" className="text-primary inline-flex items-center gap-1 font-medium hover:underline" data-testid="goto-watchlist">
                                My watchlist <ArrowRight className="h-3.5 w-3.5" />
                            </Link>
                        </div>
                    </div>
                    <ScreenerTable
                        rows={rows}
                        sort={sort}
                        onSort={handleSort}
                        watchlist={watchlist}
                        onToggleWatch={toggleWatch}
                    />

                    {/* PAGINATION */}
                    {total > pageSize && (
                        <Pagination
                            page={page}
                            total={total}
                            pageSize={pageSize}
                            onPage={(p) => {
                                setPage(p);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function InlinePager({ page, total, pageSize, onPage }) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const current = page + 1;
    return (
        <div className="flex items-center gap-1.5 text-xs" data-testid="pagination-inline">
            <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={page === 0}
                onClick={() => onPage(page - 1)}
                data-testid="page-prev-top"
                aria-label="Previous page"
            >
                <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="text-muted-foreground tabular-nums px-1">
                Page <span className="font-medium text-foreground">{current}</span> / {totalPages}
            </span>
            <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={current >= totalPages}
                onClick={() => onPage(page + 1)}
                data-testid="page-next-top"
                aria-label="Next page"
            >
                <ChevronRight className="h-3.5 w-3.5" />
            </Button>
        </div>
    );
}

function Pagination({ page, total, pageSize, onPage }) {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const current = page + 1;

    const pages = pageNumbers(current, totalPages);

    return (
        <div
            className="mt-5 flex items-center justify-between flex-wrap gap-3"
            data-testid="pagination"
        >
            <div className="text-xs text-muted-foreground tabular-nums">
                Page {current} of {totalPages}
            </div>
            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => onPage(page - 1)}
                    data-testid="page-prev"
                >
                    <ChevronLeft className="h-3.5 w-3.5" />
                    Prev
                </Button>
                {pages.map((p, i) =>
                    p === "…" ? (
                        <span key={`gap-${i}`} className="px-2 text-muted-foreground">…</span>
                    ) : (
                        <Button
                            key={p}
                            variant={p === current ? "default" : "ghost"}
                            size="sm"
                            className="min-w-9"
                            onClick={() => onPage(p - 1)}
                            data-testid={`page-${p}`}
                        >
                            {p}
                        </Button>
                    )
                )}
                <Button
                    variant="outline"
                    size="sm"
                    disabled={current >= totalPages}
                    onClick={() => onPage(page + 1)}
                    data-testid="page-next"
                >
                    Next
                    <ChevronRight className="h-3.5 w-3.5" />
                </Button>
            </div>
        </div>
    );
}

// Build a windowed page list: 1 … 4 5 [6] 7 8 … 22
function pageNumbers(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const out = new Set([1, total, current, current - 1, current + 1]);
    if (current <= 3) [2, 3, 4].forEach((p) => out.add(p));
    if (current >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => out.add(p));
    const sorted = Array.from(out).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
    const withGaps = [];
    sorted.forEach((p, i) => {
        if (i > 0 && p - sorted[i - 1] > 1) withGaps.push("…");
        withGaps.push(p);
    });
    return withGaps;
}
